import React, { useEffect, useMemo, useState } from 'react';

import * as Core from '../../core';
import Markdown from '../../components/markdown';
import { useLeague, useProfile } from '../../hooks/store';
import { LeagueService } from '../../services/leagueService';

import './ncbcGames.scss';

const byName = (a: Core.Models.LeagueComingSoonAsset, b: Core.Models.LeagueComingSoonAsset) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

const NcbcGames = (): JSX.Element => {
    const league = useLeague();
    const profile = useProfile();
    const isLoggedIn = !!profile;
    const isNcbc = league?.featureGroup === Core.Models.FeatureGroup.NCBC;
    const [assets, setAssets] = useState<Core.Models.LeagueComingSoonAsset[] | undefined>(undefined);
    const [selected, setSelected] = useState<Core.Models.LeagueComingSoonAsset | undefined>(undefined);

    useEffect(() => {
        if (!isNcbc) return;

        let cancelled = false;
        LeagueService.getComingSoonAssets()
            .then((data) => {
                if (!cancelled) setAssets(data);
            })
            .catch(() => {
                if (!cancelled) setAssets([]);
            });

        return () => {
            cancelled = true;
        };
    }, [isNcbc]);

    useEffect(() => {
        if (!selected) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setSelected(undefined);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selected]);

    const launchGames = useMemo(() => (assets || []).filter((asset) => asset.isLaunch).sort(byName), [assets]);
    const comingSoon = useMemo(() => (assets || []).filter((asset) => !asset.isLaunch).sort(byName), [assets]);

    // Guests see marketing launch / coming-soon art; logged-in players use Solo Play instead.
    if (!isNcbc || isLoggedIn || !assets) return <></>;

    return (
        <section className="ncbc-games" aria-labelledby="ncbc-launch-games-title">
            <div className="ncbc-games__group">
                <h2 className="ncbc-games__title" id="ncbc-launch-games-title">
                    Launch games
                </h2>
                <p className="ncbc-games__lede">
                    These games will be available at launch. <strong>Click a game to learn more!</strong>
                </p>
                {launchGames.length === 0 ? (
                    <p className="ncbc-games__empty">Launch games will be posted here.</p>
                ) : (
                    <div className="ncbc-games__grid">
                        {launchGames.map((asset) => (
                            <button
                                className="ncbc-games__card"
                                key={asset.id}
                                onClick={() => setSelected(asset)}
                                type="button"
                            >
                                <img alt="" src={asset.imageUrl} />
                                <span className="ncbc-games__shade" />
                                <span className="ncbc-games__name">{asset.name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="ncbc-games__group">
                <h2 className="ncbc-games__title">Coming soon</h2>
                <p className="ncbc-games__lede">These games will be available soon after launch.</p>
                {comingSoon.length === 0 ? (
                    <p className="ncbc-games__empty">Coming soon games will be posted here.</p>
                ) : (
                    <div className="ncbc-games__grid">
                        {comingSoon.map((asset) => (
                            <div className="ncbc-games__card ncbc-games__card--unavailable" key={asset.id}>
                                <img alt="" src={asset.imageUrl} />
                                <span className="ncbc-games__shade" />
                                <span className="ncbc-games__name">{asset.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {selected && (
                <div className="ncbc-games__overlay" onClick={() => setSelected(undefined)} role="presentation">
                    <div
                        aria-labelledby="ncbc-game-dialog-title"
                        aria-modal="true"
                        className="ncbc-games__dialog"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                    >
                        <button className="ncbc-games__close" onClick={() => setSelected(undefined)} type="button">
                            Close
                        </button>
                        <h3 className="ncbc-games__dialog-title" id="ncbc-game-dialog-title">
                            {selected.name}
                        </h3>
                        <div className="ncbc-games__dialog-body">
                            <div className="ncbc-games__description">
                                {selected.description ? (
                                    <Markdown source={selected.description} />
                                ) : (
                                    <p>No description yet.</p>
                                )}
                            </div>
                            {selected.rubricImageUrl && (
                                <img alt={`${selected.name} rubric`} className="ncbc-games__rubric" src={selected.rubricImageUrl} />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default NcbcGames;
